import {
    sortTiersByMinQuantity,
    tiersToThresholds,
    validateTierForm,
} from '../../constants/pricingTierConfig';

/**
 * Volume pricing for the "precios-mayor" step. It maps 1:1 to the CMS "Descuento por cantidad"
 * rule (Promociones → POST /stores/:id/pricing-tiers): scope `store`, discount type `percentage`,
 * so it applies to every product the merchant adds later. Rows use the CMS shape
 * ({ localId, min_quantity, discount_value }) so the CMS validators can be reused as-is.
 */

export const WHOLESALE_MAX_LEVELS = 5;
export const WHOLESALE_DISCOUNT_TYPE = 'percentage';
export const WHOLESALE_SCOPE = 'store';

let nextId = 0;
export const createWholesaleRow = (overrides = {}) => {
    nextId += 1;
    return { localId: `mayor-${Date.now()}-${nextId}`, min_quantity: '', discount_value: '', ...overrides };
};

const isBlank = (value) => value === '' || value === null || value === undefined;

/** Rows the merchant actually filled (an untouched row is ignored, not an error). */
export const filledRows = (rows = []) =>
    rows.filter((row) => !isBlank(row.min_quantity) || !isBlank(row.discount_value));

/** Same validation the CMS applies before saving a quantity-discount rule. */
export const validateWholesaleRows = (rows = []) => {
    const filled = filledRows(rows);
    if (filled.length === 0) return { valid: false, empty: true };
    const check = validateTierForm(filled, WHOLESALE_DISCOUNT_TYPE);
    if (check.valid && filled.some((row) => isDiscountTooHigh(row.discount_value))) {
        return { valid: false, messageKey: 'SignUpV2.mayor.discountMax' };
    }
    return check;
};

/** The CMS rule allows 100%, which would make the products free: the onboarding stops below it. */
export const WHOLESALE_MAX_DISCOUNT = 99;
export const isDiscountTooHigh = (value) => Number(value) > WHOLESALE_MAX_DISCOUNT;

/** API thresholds: [{ min_quantity, discount_value }] sorted and deduped (CMS helper). */
export const wholesaleThresholds = (rows = []) => tiersToThresholds(filledRows(rows));

/** Unit price after a percentage discount, rounded to whole pesos like the storefront. */
export const discountedUnitPrice = (basePrice, percentage) => {
    const base = Number(basePrice);
    const pct = Number(percentage);
    if (!Number.isFinite(base) || base <= 0) return null;
    if (!Number.isFinite(pct) || pct <= 0) return base;
    return Math.round(base * (1 - Math.min(100, pct) / 100));
};

/** Lowest valid level, used for the "Desde 12 u. a $71.200" hint in the previews. */
export const firstWholesaleLevel = (rows = [], basePrice) => {
    const sorted = sortTiersByMinQuantity(filledRows(rows)).filter(
        (row) => Number(row.min_quantity) >= 1 && Number(row.discount_value) > 0
    );
    if (sorted.length === 0) return null;
    const first = sorted[0];
    return {
        minQty: Number(first.min_quantity),
        discount: Number(first.discount_value),
        unitPrice: discountedUnitPrice(basePrice, first.discount_value),
    };
};

export const isWholesaleType = (storeType) => storeType === 'wholesale' || storeType === 'both';
